import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Send, Clock, CheckCircle, User, Calendar, Filter, Search, Bot, Sparkles } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

interface Question {
  id: number;
  user_id: string;
  category: string;
  question: string;
  language: string;
  status: 'pending' | 'answered';
  response?: string;
  created_at: string;
  updated_at: string;
}

interface ChatMessage {
  id: string;
  text: string;
  isBot: boolean;
  timestamp: Date;
}

const categories = [
  { value: 'periods', label: 'Periods & Menstruation', color: 'from-pink-500 to-rose-500' },
  { value: 'fertility', label: 'Fertility & Ovulation', color: 'from-green-500 to-emerald-500' },
  { value: 'pregnancy', label: 'Pregnancy & Conception', color: 'from-blue-500 to-cyan-500' },
  { value: 'symptoms', label: 'Symptoms & Pain', color: 'from-orange-500 to-amber-500' },
  { value: 'general', label: 'General Health', color: 'from-purple-500 to-indigo-500' }
];

const AskDoctor: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState<'all' | 'pending' | 'answered'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'questions' | 'ai-chat'>('ai-chat');
  
  // AI Chat state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const [formData, setFormData] = useState({
    category: '',
    question: '',
    language: i18n.language,
  });

  useEffect(() => {
    fetchQuestions();
  }, [user]);

  useEffect(() => {
    if (activeTab === 'ai-chat' && chatMessages.length === 0) {
      // Initial greeting message
      const greeting = getGreetingMessage();
      setChatMessages([{
        id: '1',
        text: greeting,
        isBot: true,
        timestamp: new Date()
      }]);
    }
  }, [activeTab, i18n.language]);

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getGreetingMessage = () => {
    const greetings = {
      en: "Hi! I'm your AI period health assistant. I can help answer questions about menstrual cycles, symptoms, and general reproductive health. What would you like to know?",
      si: "ආයුබෝවන්! මම ඔබේ AI ඔසප් සෞඛ්‍ය සහායකයා. මට ඔසප් චක්‍ර, රෝග ලක්ෂණ සහ සාමාන්‍ය ප්‍රජනන සෞඛ්‍යය පිළිබඳ ප්‍රශ්න වලට උත්තර දීමට උපකාර කළ හැක. ඔබ දැන ගැනීමට කැමති කුමක්ද?",
      ta: "வணக்கம்! நான் உங்கள் AI மாதவிடாய் சுகாதார உதவியாளர். மாதவிடாய் சுழற்சிகள், அறிகுறிகள் மற்றும் பொது இனப்பெருக்க ஆரோக்கியம் பற்றிய கேள்விகளுக்கு பதிலளிக்க என்னால் உதவ முடியும். நீங்கள் என்ன தெரிந்து கொள்ள விரும்புகிறீர்கள்?"
    };
    return greetings[i18n.language as keyof typeof greetings] || greetings.en;
  };

  const generateResponse = (userMessage: string): string => {
    const message = userMessage.toLowerCase();
    
    // Period-related responses
    if (message.includes('period') || message.includes('menstruation') || message.includes('ඔසප්') || message.includes('மாதவிடாய்')) {
      if (message.includes('late') || message.includes('missed') || message.includes('ප්‍රමාද') || message.includes('தாமதம்')) {
        return getResponse('late_period');
      }
      if (message.includes('heavy') || message.includes('බර') || message.includes('கனமான')) {
        return getResponse('heavy_period');
      }
      if (message.includes('irregular') || message.includes('අක්‍රමවත්') || message.includes('ஒழுங்கற்ற')) {
        return getResponse('irregular_period');
      }
      return getResponse('general_period');
    }

    // Pain and symptoms
    if (message.includes('pain') || message.includes('cramp') || message.includes('වේදනා') || message.includes('வலி')) {
      return getResponse('period_pain');
    }

    // Cycle tracking
    if (message.includes('track') || message.includes('cycle') || message.includes('සටහන්') || message.includes('சுழற்சி')) {
      return getResponse('cycle_tracking');
    }

    // PMS symptoms
    if (message.includes('pms') || message.includes('mood') || message.includes('මානසික') || message.includes('மனநிலை')) {
      return getResponse('pms_symptoms');
    }

    // Fertility and ovulation
    if (message.includes('fertile') || message.includes('ovulation') || message.includes('සාරවත්') || message.includes('கருவுறுதல்')) {
      return getResponse('fertility');
    }

    // Birth control
    if (message.includes('birth control') || message.includes('contraceptive') || message.includes('ගර්භනිරෝධක') || message.includes('கருத்தடை')) {
      return getResponse('birth_control');
    }

    // Default response
    return getResponse('default');
  };

  const getResponse = (type: string): string => {
    const responses = {
      en: {
        late_period: "A late period can be caused by stress, weight changes, hormones, or pregnancy. If you're sexually active, consider taking a pregnancy test. If your period is more than a week late and the test is negative, consult a healthcare provider.",
        heavy_period: "Heavy periods (lasting more than 7 days or requiring frequent pad/tampon changes) can be normal for some, but may indicate hormonal imbalances or other conditions. Track your flow and consult a doctor if concerned.",
        irregular_period: "Irregular periods are common, especially during puberty and perimenopause. Stress, weight changes, and medical conditions can affect regularity. Track your cycles and consult a healthcare provider if patterns concern you.",
        general_period: "Menstrual cycles typically last 21-35 days, with periods lasting 3-7 days. Normal flow varies by person. Track your cycle to understand your patterns and identify any changes.",
        period_pain: "Period pain (dysmenorrhea) is common. Try heat therapy, gentle exercise, and over-the-counter pain relievers. Severe pain that interferes with daily activities should be evaluated by a healthcare provider.",
        cycle_tracking: "Tracking your cycle helps identify patterns and predict your next period. Note start/end dates, flow intensity, symptoms, and mood. Our app makes this easy with calendar tracking and insights.",
        pms_symptoms: "PMS symptoms like mood changes, bloating, and breast tenderness are common before periods. Regular exercise, healthy diet, and stress management can help. Severe symptoms may indicate PMDD.",
        fertility: "You're most fertile around ovulation, typically 12-16 days before your next period. Signs include changes in cervical mucus and slight temperature increase. Track these signs if trying to conceive.",
        birth_control: "Many birth control options are available, including pills, IUDs, implants, and barrier methods. Each has different benefits and side effects. Consult a healthcare provider to find what's best for you.",
        default: "I'm here to help with period and reproductive health questions. You can ask about cycle tracking, symptoms, pain management, or general menstrual health. For serious concerns, always consult a healthcare provider."
      },
      si: {
        late_period: "ප්‍රමාද වූ ඔසප් ආතතිය, බර වෙනස්කම්, හෝමෝන හෝ ගර්භණී වීම නිසා සිදු විය හැක. ඔබ ලිංගිකව ක්‍රියාකාරී නම්, ගර්භණී පරීක්ෂණයක් කරන්න. සතියකට වඩා ප්‍රමාද වී පරීක්ෂණය negative නම්, වෛද්‍යවරයකු හමුවන්න.",
        heavy_period: "බර ඔසප් (දින 7කට වඩා හෝ නිතර pad/tampon වෙනස් කිරීම) සමහරුන්ට සාමාන්‍ය විය හැක, නමුත් හෝමෝන අසමතුලිතතාවය හෝ වෙනත් තත්වයන් පෙන්නුම් කළ හැක.",
        irregular_period: "අක්‍රමවත් ඔසප් සාමාන්‍ය වේ, විශේෂයෙන් වැඩිවිය සහ perimenopause කාලයේ. ආතතිය, බර වෙනස්කම් සහ වෛද්‍ය තත්වයන් නිතිපතා බලපායි.",
        general_period: "ඔසප් චක්‍ර සාමාන්‍යයෙන් දින 21-35 අතර පවතී, ඔසප් දින 3-7 පවතී. සාමාන්‍ය ගලායාම පුද්ගලයා අනුව වෙනස් වේ.",
        period_pain: "ඔසප් වේදනාව (dysmenorrhea) සාමාන්‍ය වේ. තාප ප්‍රතිකාර, මෘදු ව්‍යායාම සහ වේදනා නාශක උත්සාහ කරන්න.",
        cycle_tracking: "ඔබේ චක්‍රය සටහන් කිරීම රටා හඳුනා ගැනීමට සහ ඊළඟ ඔසප් පුරෝකථනය කිරීමට උපකාරී වේ.",
        pms_symptoms: "PMS රෝග ලක්ෂණ වන මානසික වෙනස්කම්, බඩ පිම්බීම සහ පියයුරු සංවේදීතාව ඔසප් වලට පෙර සාමාන්‍ය වේ.",
        fertility: "ඔබ ovulation වටා වඩාත් සාරවත් වේ, සාමාන්‍යයෙන් ඔබේ ඊළඟ ඔසප් වලට දින 12-16 කට පෙර.",
        birth_control: "බොහෝ ගර්භනිරෝධක විකල්ප ඇත, pills, IUDs, implants සහ barrier methods ඇතුළුව.",
        default: "මම ඔසප් සහ ප්‍රජනන සෞඛ්‍ය ප්‍රශ්න සඳහා උපකාර කිරීමට මෙහි සිටිමි. ගැඹුරු සැලකිල්ල සඳහා, සෑම විටම වෛද්‍යවරයකු සමඟ සාකච්ඡා කරන්න."
      },
      ta: {
        late_period: "தாமதமான மாதவிடாய் மன அழுத்தம், எடை மாற்றங்கள், ஹார்மோன்கள் அல்லது கர்ப்பம் காரணமாக இருக்கலாம். நீங்கள் பாலியல் ரீதியாக செயலில் இருந்தால், கர்ப்ப பரிசோதனை செய்யுங்கள்.",
        heavy_period: "கனமான மாதவிடாய் (7 நாட்களுக்கு மேல் அல்லது அடிக்கடி pad/tampon மாற்றுதல்) சிலருக்கு சாதாரணமாக இருக்கலாம், ஆனால் ஹார்மோன் சமநிலையின்மை அல்லது பிற நிலைமைகளைக் குறிக்கலாம்.",
        irregular_period: "ஒழுங்கற்ற மாதவிடாய் பொதுவானது, குறிப்பாக பருவமடைதல் மற்றும் perimenopause காலத்தில். மன அழுத்தம், எடை மாற்றங்கள் மற்றும் மருத்துவ நிலைமைகள் ஒழுங்குமுறையை பாதிக்கலாம்.",
        general_period: "மாதவிடாய் சுழற்சிகள் பொதுவாக 21-35 நாட்கள் நீடிக்கும், மாதவிடாய் 3-7 நாட்கள் நீடிக்கும். சாதாரண ஓட்டம் நபருக்கு நபர் மாறுபடும்.",
        period_pain: "மாதவிடாய் வலி (dysmenorrhea) பொதுவானது. வெப்ப சிகிச்சை, மென்மையான உடற்பயிற்சி மற்றும் வலி நிவாரணிகளை முயற்சிக்கவும்.",
        cycle_tracking: "உங்கள் சுழற்சியைக் கண்காணிப்பது வடிவங்களை அடையாளம் காணவும் உங்கள் அடுத்த மாதவிடாயை கணிக்கவும் உதவுகிறது.",
        pms_symptoms: "மனநிலை மாற்றங்கள், வீக்கம் மற்றும் மார்பக உணர்திறன் போன்ற PMS அறிகுறிகள் மாதவிடாய்க்கு முன் பொதுவானவை.",
        fertility: "நீங்கள் அண்டவிடுப்பின் போது மிகவும் கருவுறக்கூடியவர், பொதுவாக உங்கள் அடுத்த மாதவிடாய்க்கு 12-16 நாட்களுக்கு முன்.",
        birth_control: "pills, IUDs, implants மற்றும் barrier methods உட்பட பல கருத்தடை விருப்பங்கள் கிடைக்கின்றன.",
        default: "மாதவிடாய் மற்றும் இனப்பெருக்க சுகாதார கேள்விகளுக்கு உதவ நான் இங்கே இருக்கிறேன். தீவிர கவலைகளுக்கு, எப்போதும் ஒரு சுகாதார வழங்குநரை அணுகவும்."
      }
    };

    const langResponses = responses[i18n.language as keyof typeof responses] || responses.en;
    return langResponses[type as keyof typeof langResponses] || langResponses.default;
  };

  const handleSendChatMessage = async () => {
    if (!chatInput.trim()) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      text: chatInput,
      isBot: false,
      timestamp: new Date()
    };

    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setIsTyping(true);

    // Simulate typing delay
    setTimeout(() => {
      const botResponse: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: generateResponse(chatInput),
        isBot: true,
        timestamp: new Date()
      };

      setChatMessages(prev => [...prev, botResponse]);
      setIsTyping(false);
    }, 1000 + Math.random() * 1000);
  };

  const handleChatKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendChatMessage();
    }
  };

  const fetchQuestions = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('doctor_questions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setQuestions(data || []);
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.category || !formData.question.trim()) {
      return;
    }

    try {
      setSubmitting(true);
      const { data, error } = await supabase
        .from('doctor_questions')
        .insert([{
          user_id: user?.id,
          category: formData.category,
          question: formData.question.trim(),
          language: formData.language,
          status: 'pending'
        }])
        .select()
        .single();

      if (error) throw error;

      setQuestions([data, ...questions]);
      setFormData({ category: '', question: '', language: i18n.language });
      setShowForm(false);

      // Simulate doctor response after 2-5 seconds (for demo purposes)
      setTimeout(async () => {
        const mockResponse = getMockResponse(formData.category, formData.language);
        
        const { error: updateError } = await supabase
          .from('doctor_questions')
          .update({
            status: 'answered',
            response: mockResponse,
            updated_at: new Date().toISOString()
          })
          .eq('id', data.id);

        if (!updateError) {
          fetchQuestions();
        }
      }, Math.random() * 3000 + 2000);

    } catch (error) {
      console.error('Error submitting question:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const getMockResponse = (category: string, language: string) => {
    const responses = {
      en: {
        periods: "Thank you for your question about menstruation. Based on your concern, I recommend tracking your cycle regularly and maintaining a healthy lifestyle. If you experience severe pain or irregular cycles, please consult with a healthcare provider for personalized advice.",
        fertility: "Regarding fertility and ovulation, tracking your basal body temperature and cervical mucus can help identify your fertile window. A healthy diet, regular exercise, and stress management are important factors. Consider consulting a fertility specialist if you've been trying to conceive for over a year.",
        pregnancy: "For pregnancy-related questions, it's important to maintain regular prenatal care. Take prenatal vitamins, eat a balanced diet, and avoid harmful substances. Always consult with your obstetrician for specific concerns during pregnancy.",
        symptoms: "Regarding your symptoms, it's important to monitor their frequency and severity. Keep a symptom diary and note any patterns. If symptoms persist or worsen, please schedule an appointment with your healthcare provider for proper evaluation.",
        general: "For general health concerns, maintaining a balanced lifestyle with proper nutrition, regular exercise, and adequate sleep is essential. Don't hesitate to reach out to your healthcare provider for any persistent health issues."
      },
      si: {
        periods: "ඔබේ ඔසප් පිළිබඳ ප්‍රශ්නයට ස්තූතියි. ඔබේ සැලකිල්ල මත පදනම්ව, ඔබේ චක්‍රය නිතිපතා නිරීක්ෂණය කිරීම සහ සෞඛ්‍ය සම්පන්න ජීවන රටාවක් පවත්වා ගැනීම නිර්දේශ කරමි.",
        fertility: "සාරවත්භාවය සහ බිත්තර නිකුත් කිරීම සම්බන්ධයෙන්, ඔබේ මූලික ශරීර උෂ්ණත්වය සහ ගර්භාෂ ශ්ලේෂ්මල නිරීක්ෂණය කිරීම ඔබේ සාරවත් කාල පරිච්ඡේදය හඳුනා ගැනීමට උපකාරී වේ.",
        pregnancy: "ගර්භණී සම්බන්ධ ප්‍රශ්න සඳහා, නිතිපතා ප්‍රසව පූර්ව සත්කාර ලබා ගැනීම වැදගත්. ප්‍රසව පූර්ව විටමින් ගන්න, සමතුලිත ආහාර වේලක් ගන්න.",
        symptoms: "ඔබේ රෝග ලක්ෂණ සම්බන්ධයෙන්, ඒවායේ වාර ගණන සහ තීව්‍රතාවය නිරීක්ෂණය කිරීම වැදගත්. රෝග ලක්ෂණ දිනපොතක් තබා ගන්න.",
        general: "සාමාන්‍ය සෞඛ්‍ය සැලකිල්ල සඳහා, නිසි පෝෂණය, නිතිපතා ව්‍යායාම සහ ප්‍රමාණවත් නින්ද සහිත සමතුලිත ජීවන රටාවක් පවත්වා ගැනීම අත්‍යවශ්‍ය වේ."
      },
      ta: {
        periods: "மாதவிடாய் பற்றிய உங்கள் கேள்விக்கு நன்றி. உங்கள் கவலையின் அடிப்படையில், உங்கள் சுழற்சியை தொடர்ந்து கண்காணிக்கவும் ஆரோக்கியமான வாழ்க்கை முறையை பராமரிக்கவும் பரிந்துரைக்கிறேன்.",
        fertility: "கருவுறுதல் மற்றும் அண்டவிடுப்பு தொடர்பாக, உங்கள் அடிப்படை உடல் வெப்பநிலை மற்றும் கர்ப்பப்பை வாய் சளியை கண்காணிப்பது உங்கள் கருவுறுதல் காலத்தை அடையாளம் காண உதவும்.",
        pregnancy: "கர்ப்பம் தொடர்பான கேள்விகளுக்கு, தொடர்ந்து மகப்பேறுக்கு முந்தைய பராமரிப்பை பராமரிப்பது முக்கியம். மகப்பேறுக்கு முந்தைய வைட்டமின்களை எடுத்துக் கொள்ளுங்கள்.",
        symptoms: "உங்கள் அறிகுறிகள் தொடர்பாக, அவற்றின் அதிர்வெண் மற்றும் தீவிரத்தை கண்காணிப்பது முக்கியம். அறிகுறி நாட்குறிப்பை வைத்துக் கொள்ளுங்கள்.",
        general: "பொது சுகாதார கவலைகளுக்கு, சரியான ஊட்டச்சத்து, வழக்கமான உடற்பயிற்சி மற்றும் போதுமான தூக்கத்துடன் சமநிலையான வாழ்க்கை முறையை பராமரிப்பது அவசியம்."
      }
    };

    return responses[language as keyof typeof responses]?.[category as keyof typeof responses.en] || responses.en[category as keyof typeof responses.en];
  };

  const filteredQuestions = questions.filter(q => {
    const matchesFilter = filter === 'all' || q.status === filter;
    const matchesSearch = searchTerm === '' || 
      q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.category.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getCategoryInfo = (categoryValue: string) => {
    return categories.find(cat => cat.value === categoryValue) || categories[0];
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="max-w-6xl mx-auto"
    >
      <div className="mb-8">
        <motion.h1 
          className="text-3xl font-bold text-gray-900 mb-2"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          Ask a Doctor
        </motion.h1>
        <motion.p 
          className="text-gray-600"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          Get professional medical advice about your menstrual health and wellness concerns.
        </motion.p>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-xl">
        <motion.button
          onClick={() => setActiveTab('ai-chat')}
          className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
            activeTab === 'ai-chat'
              ? 'bg-white text-purple-700 shadow-sm'
              : 'text-gray-600 hover:text-purple-600'
          }`}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Bot className="w-5 h-5" />
          <span>AI Assistant</span>
          <motion.div
            className="w-2 h-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </motion.button>
        
        <motion.button
          onClick={() => setActiveTab('questions')}
          className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
            activeTab === 'questions'
              ? 'bg-white text-purple-700 shadow-sm'
              : 'text-gray-600 hover:text-purple-600'
          }`}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <MessageCircle className="w-5 h-5" />
          <span>Ask Doctor</span>
        </motion.button>
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'ai-chat' && (
          <motion.div
            key="ai-chat"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 overflow-hidden"
          >
            {/* Chat Header */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-500 p-6 text-white">
              <div className="flex items-center space-x-3">
                <motion.div
                  className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center"
                  animate={{ rotate: isTyping ? 360 : 0 }}
                  transition={{ duration: 2, repeat: isTyping ? Infinity : 0, ease: "linear" }}
                >
                  <Bot className="w-6 h-6" />
                </motion.div>
                <div>
                  <h3 className="text-xl font-semibold">AI Period Health Assistant</h3>
                  <p className="text-purple-100">
                    {isTyping ? 'Typing...' : 'Online • Instant responses'}
                  </p>
                </div>
                <motion.div
                  className="ml-auto"
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Sparkles className="w-6 h-6 text-yellow-300" />
                </motion.div>
              </div>
            </div>

            {/* Chat Messages */}
            <div className="h-96 overflow-y-auto p-6 space-y-4">
              <AnimatePresence>
                {chatMessages.map((message) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${message.isBot ? 'justify-start' : 'justify-end'}`}
                  >
                    <div className={`flex items-start space-x-3 max-w-[80%] ${message.isBot ? '' : 'flex-row-reverse space-x-reverse'}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        message.isBot 
                          ? 'bg-gradient-to-br from-purple-500 to-pink-500' 
                          : 'bg-gray-300'
                      }`}>
                        {message.isBot ? (
                          <Bot className="w-4 h-4 text-white" />
                        ) : (
                          <User className="w-4 h-4 text-gray-600" />
                        )}
                      </div>
                      <div className={`px-4 py-3 rounded-2xl ${
                        message.isBot
                          ? 'bg-purple-50 text-gray-800 border border-purple-100'
                          : 'bg-gradient-to-r from-purple-600 to-pink-500 text-white'
                      }`}>
                        <p className="text-sm leading-relaxed">{message.text}</p>
                        <p className={`text-xs mt-2 ${
                          message.isBot ? 'text-purple-400' : 'text-purple-100'
                        }`}>
                          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
              
              {isTyping && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-start"
                >
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="px-4 py-3 bg-purple-50 rounded-2xl border border-purple-100">
                      <div className="flex space-x-1">
                        <motion.div
                          className="w-2 h-2 bg-purple-400 rounded-full"
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                        />
                        <motion.div
                          className="w-2 h-2 bg-purple-400 rounded-full"
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                        />
                        <motion.div
                          className="w-2 h-2 bg-purple-400 rounded-full"
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Chat Input */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center space-x-3">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyPress={handleChatKeyPress}
                  placeholder="Ask about periods, symptoms, cycle tracking, or any health concerns..."
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                />
                <motion.button
                  onClick={handleSendChatMessage}
                  disabled={!chatInput.trim() || isTyping}
                  className="p-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Send className="w-5 h-5" />
                </motion.button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Press Enter to send • This AI provides general information and should not replace professional medical advice
              </p>
            </div>
          </motion.div>
        )}

        {activeTab === 'questions' && (
          <motion.div
            key="questions"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            {/* Action Bar */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <motion.button
                onClick={() => setShowForm(true)}
                className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <MessageCircle size={18} />
                <span>Ask New Question</span>
              </motion.button>

              <div className="flex items-center space-x-4">
                {/* Search */}
                <motion.div 
                  className="relative"
                  initial={{ x: 20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search questions..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm"
                  />
                </motion.div>

                {/* Filter */}
                <motion.div
                  initial={{ x: 20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value as any)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm"
                  >
                    <option value="all">All Questions</option>
                    <option value="pending">Pending</option>
                    <option value="answered">Answered</option>
                  </select>
                </motion.div>
              </div>
            </div>

            {/* Questions List */}
            <motion.div 
              className="space-y-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              {loading ? (
                <div className="flex justify-center items-center h-32">
                  <motion.div
                    className="w-8 h-8 border-4 border-purple-200 border-t-purple-600 rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  />
                </div>
              ) : filteredQuestions.length > 0 ? (
                <AnimatePresence>
                  {filteredQuestions.map((question, index) => {
                    const categoryInfo = getCategoryInfo(question.category);
                    return (
                      <motion.div
                        key={question.id}
                        className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 hover:shadow-xl transition-all duration-300"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ y: -2 }}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <motion.div
                              className={`p-2 bg-gradient-to-br ${categoryInfo.color} rounded-lg`}
                              whileHover={{ scale: 1.1, rotate: 5 }}
                            >
                              <MessageCircle className="w-5 h-5 text-white" />
                            </motion.div>
                            <div>
                              <h3 className="font-medium text-gray-900">{categoryInfo.label}</h3>
                              <div className="flex items-center space-x-2 text-sm text-gray-500">
                                <Calendar className="w-4 h-4" />
                                <span>{new Date(question.created_at).toLocaleDateString()}</span>
                              </div>
                            </div>
                          </div>
                          <motion.div
                            className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium ${
                              question.status === 'answered'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2 + index * 0.1 }}
                          >
                            {question.status === 'answered' ? (
                              <CheckCircle className="w-4 h-4" />
                            ) : (
                              <Clock className="w-4 h-4" />
                            )}
                            <span>{question.status === 'answered' ? 'Answered' : 'Pending'}</span>
                          </motion.div>
                        </div>

                        <div className="mb-4">
                          <h4 className="font-medium text-gray-900 mb-2">Your Question:</h4>
                          <p className="text-gray-700 bg-gray-50 rounded-lg p-3">
                            {question.question}
                          </p>
                        </div>

                        {question.response && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="border-t border-gray-200 pt-4"
                          >
                            <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                              <User className="w-4 h-4 mr-2 text-purple-600" />
                              Doctor's Response:
                            </h4>
                            <div className="bg-purple-50 rounded-lg p-4">
                              <p className="text-gray-700">{question.response}</p>
                            </div>
                          </motion.div>
                        )}
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              ) : (
                <motion.div 
                  className="text-center py-12"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <motion.div
                    className="w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-4"
                    whileHover={{ scale: 1.1, rotate: 10 }}
                  >
                    <MessageCircle className="w-8 h-8 text-purple-500" />
                  </motion.div>
                  <p className="text-gray-500 mb-4">No questions found</p>
                  <motion.button
                    onClick={() => setShowForm(true)}
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Ask Your First Question
                  </motion.button>
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Question Form Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowForm(false)}
          >
            <motion.div
              className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl w-full max-w-lg border border-white/20"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Ask a Question</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <div className="grid grid-cols-1 gap-2">
                      {categories.map((category) => (
                        <motion.label
                          key={category.value}
                          className={`flex items-center p-3 border rounded-xl cursor-pointer transition-all duration-300 ${
                            formData.category === category.value
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-gray-200 hover:border-purple-300'
                          }`}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <input
                            type="radio"
                            name="category"
                            value={category.value}
                            checked={formData.category === category.value}
                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                            className="sr-only"
                          />
                          <div className={`w-4 h-4 rounded-full border-2 mr-3 ${
                            formData.category === category.value
                              ? 'border-purple-500 bg-purple-500'
                              : 'border-gray-300'
                          }`}>
                            {formData.category === category.value && (
                              <motion.div
                                className="w-2 h-2 bg-white rounded-full m-0.5"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                              />
                            )}
                          </div>
                          <span className="text-sm font-medium">{category.label}</span>
                        </motion.label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="question" className="block text-sm font-medium text-gray-700 mb-2">
                      Your Question
                    </label>
                    <motion.textarea
                      id="question"
                      value={formData.question}
                      onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 resize-none"
                      placeholder="Describe your concern or question in detail..."
                      whileFocus={{ scale: 1.01 }}
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="language" className="block text-sm font-medium text-gray-700 mb-2">
                      Preferred Response Language
                    </label>
                    <select
                      id="language"
                      value={formData.language}
                      onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                    >
                      <option value="en">English</option>
                      <option value="si">සිංහල</option>
                      <option value="ta">தமிழ்</option>
                    </select>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                    <motion.button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-300"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      type="submit"
                      disabled={submitting || !formData.category || !formData.question.trim()}
                      className={`flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl font-medium shadow-lg transition-all duration-300 ${
                        submitting || !formData.category || !formData.question.trim()
                          ? 'opacity-70 cursor-not-allowed'
                          : 'hover:shadow-xl hover:scale-105'
                      }`}
                      whileHover={!submitting ? { scale: 1.05 } : {}}
                      whileTap={!submitting ? { scale: 0.95 } : {}}
                    >
                      {submitting ? (
                        <motion.div
                          className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        />
                      ) : (
                        <Send size={16} />
                      )}
                      <span>{submitting ? 'Submitting...' : 'Submit Question'}</span>
                    </motion.button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default AskDoctor;